import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, FIELD_TYPES } from '../../core/constants';
// HARDCODED — catalog now comes from GET /document-templates via docTpls
// import { DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon } from '../../components/Icon';


function DocTemplatesSettings() {
  const { docTpls, setDocTpls, showToast } = useApp();
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(null);

  const startEdit = (tpl) => { setEditing(tpl.id); setForm(JSON.parse(JSON.stringify(tpl))); };
  const startNew  = () => {
    const id = uuid();
    const blank = { id, name:"New Template", collectionType:"common", infoFields:[], docSlots:[] };
    setDocTpls(t => ({ ...t, [id]: blank }));
    startEdit(blank);
  };
  const save = () => {
    if (!form.name) { showToast("Template name required","error"); return; }
    setDocTpls(t => ({ ...t, [form.id]: form }));
    setEditing(null);
    showToast("Template saved!","success");
  };
  const duplicateTpl = (tpl) => {
    const copy = JSON.parse(JSON.stringify(tpl));
    copy.id = uuid();
    copy.name = `${tpl.name} (Copy)`;
    setDocTpls(t => ({ ...t, [copy.id]: copy }));
    startEdit(copy);
    showToast("Template duplicated — edit and save.", "success");
  };
  const deleteTpl = (id) => {
    if (!window.confirm("Delete this template? Tasks using it will lose their field config.")) return;
    setDocTpls(t => { const n = {...t}; delete n[id]; return n; });
    showToast("Template deleted","warning");
  };

  const setF  = (k,v) => setForm(f => ({...f,[k]:v}));
  const addField = () => setForm(f => ({ ...f, infoFields:[...f.infoFields,{id:uuid(),label:"",type:"text",required:true,options:""}] }));
  const addSlot  = () => setForm(f => ({ ...f, docSlots:[...f.docSlots,{id:uuid(),label:"",required:true}] }));
  const updField = (idx,k,v) => setForm(f => { const a=[...f.infoFields]; a[idx]={...a[idx],[k]:v}; return {...f,infoFields:a}; });
  const updSlot  = (idx,k,v) => setForm(f => { const a=[...f.docSlots];   a[idx]={...a[idx],[k]:v}; return {...f,docSlots:a};   });
  const delField = (idx) => setForm(f => ({ ...f, infoFields:f.infoFields.filter((_,i)=>i!==idx) }));
  const delSlot  = (idx) => setForm(f => ({ ...f, docSlots:f.docSlots.filter((_,i)=>i!==idx)   }));

  if (editing && form) return (
    <div style={{ maxWidth:780 }}>
      <button className="btn" style={{ marginBottom:16 }} onClick={()=>setEditing(null)}>← Back</button>
      <div className="card">
        <div className="card-head"><div className="card-title">Edit Template</div></div>
        <div className="card-body">
          {/* Template meta */}
          <div className="form-grid-2" style={{ marginBottom:20 }}>
            <div className="f-group">
              <label className="f-label">Template Name <span className="f-req">*</span></label>
              <input className="f-input" value={form.name} onChange={e=>setF("name",e.target.value)} placeholder="e.g. DSC Creation" />
            </div>
            <div className="f-group">
              <label className="f-label">Collection Level</label>
              <select className="f-select" value={form.collectionType} onChange={e=>setF("collectionType",e.target.value)}>
                <option value="common">Common — collected once per company</option>
                <option value="director">Director-level — once per director (repeats)</option>
              </select>
            </div>
          </div>

          {/* Info fields */}
          <div style={{ marginBottom:24 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
              <div style={{ fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:6 }}><Icon name="edit" size={14} /> Information Fields ({form.infoFields.length})</div>
              <button className="btn btn-ghost btn-sm" onClick={addField}>+ Add Field</button>
            </div>
            {form.infoFields.length===0 && <div style={{ fontSize:12,color:"var(--muted)",padding:"12px",background:"#FAFAF8",borderRadius:8,textAlign:"center" }}>No fields yet — click Add Field</div>}
            {form.infoFields.map((f,i) => (
              <div key={f.id} style={{ display:"flex",gap:8,alignItems:"flex-start",marginBottom:8,padding:10,background:"#FAFAF8",borderRadius:8,border:"1px solid var(--border)" }}>
                <div style={{ flex:2 }}>
                  <label className="f-label" style={{ fontSize:9 }}>Label</label>
                  <input className="f-input" value={f.label} onChange={e=>updField(i,"label",e.target.value)} placeholder="e.g. Full Name" style={{ marginBottom:0 }} />
                </div>
                <div style={{ flex:1 }}>
                  <label className="f-label" style={{ fontSize:9 }}>Type</label>
                  <select className="f-select" value={f.type} onChange={e=>updField(i,"type",e.target.value)}>
                    {FIELD_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                {f.type==="select" && (
                  <div style={{ flex:2 }}>
                    <label className="f-label" style={{ fontSize:9 }}>Options (comma-separated)</label>
                    <input className="f-input" value={f.options||""} onChange={e=>updField(i,"options",e.target.value)} placeholder="Option 1, Option 2" style={{ marginBottom:0 }} />
                  </div>
                )}
                <div style={{ display:"flex",flexDirection:"column",gap:4,alignItems:"center",paddingTop:18 }}>
                  <label style={{ display:"flex",alignItems:"center",gap:4,fontSize:11,cursor:"pointer" }}>
                    <input type="checkbox" checked={f.required} onChange={e=>updField(i,"required",e.target.checked)} />
                    Required
                  </label>
                  <button className="btn btn-sm btn-red" onClick={()=>delField(i)}><Icon name="close" size={13} /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Doc slots */}
          <div>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
              <div style={{ fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:6 }}><Icon name="folder" size={14} /> Document Slots ({form.docSlots.length})</div>
              <button className="btn btn-ghost btn-sm" onClick={addSlot}>+ Add Document Slot</button>
            </div>
            {form.docSlots.length===0 && <div style={{ fontSize:12,color:"var(--muted)",padding:"12px",background:"#FAFAF8",borderRadius:8,textAlign:"center" }}>No document slots yet — click Add Document Slot</div>}
            {form.docSlots.map((s,i) => (
              <div key={s.id} style={{ display:"flex",gap:8,alignItems:"center",marginBottom:8,padding:10,background:"#FAFAF8",borderRadius:8,border:"1px solid var(--border)" }}>
                <div style={{ flex:1 }}>
                  <input className="f-input" value={s.label} onChange={e=>updSlot(i,"label",e.target.value)} placeholder="e.g. PAN Card" style={{ marginBottom:0 }} />
                </div>
                <label style={{ display:"flex",alignItems:"center",gap:4,fontSize:12,cursor:"pointer",whiteSpace:"nowrap" }}>
                  <input type="checkbox" checked={s.required} onChange={e=>updSlot(i,"required",e.target.checked)} />
                  Required
                </label>
                <button className="btn btn-sm btn-red" onClick={()=>delSlot(i)}><Icon name="close" size={13} /></button>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding:"0 20px 20px",display:"flex",gap:10 }}>
          <button className="btn" onClick={()=>setEditing(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save Template</button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
        <div style={{ fontSize:13,color:"var(--muted)" }}>
          Define what information and documents are collected per task type. These templates auto-apply when tasks are created from invoices.
        </div>
        <button className="btn btn-primary" onClick={startNew}>+ New Template</button>
      </div>
      <div className="grid2">
        {Object.values(docTpls).map(tpl => (
          <div key={tpl.id} className="card">
            <div className="card-head">
              <div>
                <div className="card-title">{tpl.name}</div>
                <div style={{ display:"flex",gap:8,marginTop:4 }}>
                  <span className="tag">{tpl.collectionType==="director"?"Per Director":"Per Company"}</span>
                  <span className="tag">{tpl.infoFields.length} fields</span>
                  <span className="tag">{tpl.docSlots.length} doc slots</span>
                </div>
              </div>
              <div style={{ display:"flex",gap:8 }}>
                <button className="btn btn-sm" onClick={()=>startEdit(tpl)}>Edit</button>
                <button className="btn btn-sm" onClick={()=>duplicateTpl(tpl)}>Duplicate</button>
                <button className="btn btn-sm btn-red" onClick={()=>deleteTpl(tpl.id)}>Delete</button>
              </div>
            </div>
            <div className="card-body" style={{ padding:0 }}>
              {tpl.infoFields.slice(0,4).map(f=>(
                <div key={f.id} className="row-item" style={{ padding:"8px 16px" }}>
                  <Icon name="edit" size={13} />
                  <div style={{ flex:1,fontSize:12 }}>{f.label}</div>
                  <span style={{ fontSize:10,color:f.required?"var(--red)":"var(--muted)" }}>{f.required?"Required":"Optional"}</span>
                </div>
              ))}
              {tpl.infoFields.length>4 && <div style={{ padding:"6px 16px",fontSize:11,color:"var(--muted)" }}>+{tpl.infoFields.length-4} more fields…</div>}
              {tpl.docSlots.slice(0,3).map(s=>(
                <div key={s.id} className="row-item" style={{ padding:"8px 16px",background:"#FAFAF8" }}>
                  <Icon name="doc" size={13} />
                  <div style={{ flex:1,fontSize:12 }}>{s.label}</div>
                  <span style={{ fontSize:10,color:s.required?"var(--red)":"var(--muted)" }}>{s.required?"Required":"Optional"}</span>
                </div>
              ))}
              {tpl.docSlots.length>3 && <div style={{ padding:"6px 16px",fontSize:11,color:"var(--muted)" }}>+{tpl.docSlots.length-3} more doc slots…</div>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// BATCH B — BILLING SUMMARY PAGE
// ═══════════════════════════════════════════════════════════════════════

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];


export { DocTemplatesSettings };
